import React, { useMemo, useState } from 'react'
import { Form, Formik } from 'formik'
import moment from 'moment'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import CustomButton from '../../../shared/CustomButton'
import EmrOrderItem from '../patient-profile/EmrOrderItem'
import { fetchData } from '../../../helpers/fetchData'
import { compareEmrItems } from '../../../utils/CompareItems'
import { convertPreAuthClinicalData } from '../../../constants/EmrContants'
import {
  shouldAutoSendApproval
} from '../../../utils/insuranceApprovalSettings'
import CheckPermission from '../../../hooks/CheckPermission'
import {
  isReferralOut,
  splitPreAuthPayloadByReferral
} from '../../../utils/isReferralOut'

const EditEmrItemsModal = ({
  emrData,
  visitData,
  onClose,
  onUpdated,
  selectedPatientData
}) => {
  const { hasPermission } = CheckPermission()
  const canUpdateEmr = hasPermission('approval_management', 'update') || hasPermission('emr', 'update')
  const { t } = useTranslation()
  const [isSaving, setIsSaving] = useState(false)
  const auth = useSelector(state => state.auth)
  const selectedVisitData = useSelector(state => state.visits?.selectedVisitData)

  const normalizeItemNumbers = item => {
    const { resend_approval, ...itemWithoutResend } = item || {}
    const numericFields = [
      'unit_price',
      'original_price',
      'approved_amount',
      'approved_qty',
      'quantity',
      'discount_amount',
      'patient_share',
      'tax_amount',
      'factor',
      'net',
      'patientShareAmount',
      'payer_share',
      'days_supply',
      'gross_amount',
      'patient_tax_amount',
      'patient_share_with_vat',
      'net_payable_amount',
      'invoice_no',
      'number_of_sessions'
    ]

    const normalizedNumberFields = numericFields.reduce(
      (normalizedItem, field) => {
        const value = normalizedItem?.[field]
        if (value === '' || value === null || value === undefined) {
          return normalizedItem
        }

        const numericValue = Number(value)
        if (Number.isNaN(numericValue)) {
          return normalizedItem
        }

        return {
          ...normalizedItem,
          [field]: numericValue
        }
      },
      { ...itemWithoutResend }
    )

    return {
      ...normalizedNumberFields,
      is_package:
        normalizedNumberFields?.is_package === undefined ||
          normalizedNumberFields?.is_package === null
          ? ''
          : String(normalizedNumberFields.is_package)
    }
  }

  const initialValues = useMemo(
    () => ({
      accident: emrData?.accident || {},
      careTeamsArray: emrData?.careTeamsArr || [],
      chiefComplaints: emrData?.medical_assessments?.chief_complaints || {
        category: 'chief-complaint',
        codeText: '',
        code: '',
        valueUnit: '',
        value: ''
      },
      diagnosisArray: emrData?.diagnosisArr || [],
      is_accident: emrData?.is_accident || false,
      pregnancy: emrData?.medical_case_attribute?.pregnancy || false,
      items: emrData?.items || [],
      supportingInfos: emrData?.supportingInfos || [],
      vitalSigns: emrData?.vital_signs || [],
      work_related: emrData?.medical_case_attribute?.work_related || false
    }),
    [emrData]
  )

  const handleRequestPreAuth = async (
    values,
    resolvedEmrId,
    sourceEmrData = emrData
  ) => {
    const insurancePlanId =
      sourceEmrData?.patient?.selected_plan?.insurance_plan_id ||
      sourceEmrData?.patient?.insurance_plans?.[0]?.insurance_plan_id ||
      selectedPatientData?.selected_plan?.insurance_plan_id ||
      visitData?.patient?.selected_plan?.insurance_plan_id ||
      selectedVisitData?.patient?.selected_plan?.insurance_plan_id ||
      selectedPatientData?.insurance_plans?.[0]?.insurance_plan_id ||
      visitData?.patient?.insurance_plans?.[0]?.insurance_plan_id ||
      selectedVisitData?.patient?.insurance_plans?.[0]?.insurance_plan_id ||
      ''

    if (!insurancePlanId) return

    const diagnosisObj = values?.diagnosisArray?.map(data => ({
      type: data?.type,
      on_admission:
        data?.on_admission === 'false' || data?.on_admission === false
          ? false
          : true,
      code: data?.codeId || data?.code,
      description: data?.codeDescription || data?.description
    }))
    const currentDiagnosisReferences =
      diagnosisObj?.map((_, index) => index + 1) || []

    const itemsObj = values?.items
      ?.filter(
        item =>
          item?.is_approval === true &&
          item?.item_category === 'insurance' &&
          (!item?.is_approval_sent || item?.resend_approval === true)
      )
      ?.map(item => {
        const itemType =
          item?.selected_category || item?.type || item?.item_type
        const validType =
          itemType && itemType.length < 50 && !itemType.match(/^[0-9a-f]{24}$/i)
            ? itemType
            : 'service-codes'

        return {
          _item_id: item?.item_id || null,
          is_referral_out: isReferralOut(item),
          item_tp_ref_no: item?.item_id || item?.id || null,
          source: item?.source || null,
          type: validType,
          supergit_master_code:
            item?.source === 'sfda' || item?.source === 'provider_pharmacy'
              ? item?.supergit_master_code || item?.master_code || null
              : item?.supergit_master_code || null,
          standard_code: item?.standard_code || '',
          standard_description:
            item?.standard_description || item?.item_name || '',
          non_standard_code: item?.is_sfda
            ? item?.non_standard_code || null
            : item?.agreed_code || item?.non_standard_code || null,
          non_standard_description: item?.is_sfda
            ? item?.non_standard_description || null
            : item?.agreed_description ||
            item?.non_standard_description ||
            null,
          medication_selection_reason:
            validType === 'medication-codes'
              ? item?.medication_selection_reason || 'Generic'
              : null,
          prescribed_medication:
            validType === 'medication-codes' && item?.standard_code
              ? String(item?.standard_code)
              : null,
          body_site: item?.body_site ? item?.body_site : null,
          sub_site: item?.sub_site ? item?.sub_site : '',
          maternity: item?.maternity || false,
          service_start_date: item?.service_start_date
            ? moment(item?.service_start_date).format('YYYY-MM-DD')
            : '',
          service_end_date: item?.service_end_date
            ? moment(item?.service_end_date).format('YYYY-MM-DD')
            : '',
          supporting_info: Array.isArray(item?.supporting_info)
            ? [
              ...item.supporting_info,
              2,
              3,
              4,
              5,
              6,
              7,
              8,
              9,
              10,
              11,
              12,
              13,
              14
            ]
            : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14],
          diagnosis: currentDiagnosisReferences,
          care_teams: Array.isArray(item?.care_teams) ? item.care_teams : [1],
          is_package:
            item?.is_package === 'true' || item?.is_package === true
              ? true
              : false,
          quantity: Number(item?.quantity) || 1,
          code: item?.standard_code || '',
          unit_price: Number(item?.unit_price) || 0,
          tax_amount: Number(item?.tax_amount) || 0,
          factor: Number(item?.factor) || 1,
          net: parseFloat(item?.net || 0),
          discount_amount: (() => {
            const quantity = Number(item?.quantity) || 1
            const unitPrice = Number(item?.unit_price) || 0
            const factor = Number(item?.factor) || 1
            const discountPercentage = Number(item?.discount_amount) || 0
            const totalAmount = quantity * unitPrice * factor
            return (totalAmount * discountPercentage) / 100
          })(),
          patient_share: Number(item?.patient_share) || 0,
          patient_share_amount:
            Number(item?.patientShareAmount || item?.patient_share_amount) || 0,
          payer_share: Number(item?.payer_share) || 0,
          days_supply: Number(item?.days_supply) || 1
        }
      })

    if (!itemsObj?.length) return

    const requiredCategoriesByType = {
      professional: [
        'chief-complaint',
        'vital-sign-weight',
        'vital-sign-diastolic',
        'vital-sign-height',
        'vital-sign-systolic',
        'treatment-plan',
        'history-of-present-illness',
        'patient-history',
        'physical-examination',
        'pulse',
        'oxygen-saturation',
        'temperature',
        'respiratory-rate',
        'investigation-result'
      ],
      institutional: [
        'chief-complaint',
        'vital-sign-weight',
        'vital-sign-diastolic',
        'vital-sign-height',
        'vital-sign-systolic',
        'treatment-plan',
        'history-of-present-illness',
        'patient-history',
        'physical-examination',
        'estimated-Length-of-Stay',
        'pulse',
        'oxygen-saturation',
        'temperature',
        'respiratory-rate',
        'investigation-result'
      ],
      oral: [
        'chief-complaint',
        'treatment-plan',
        'history-of-present-illness',
        'patient-history',
        'physical-examination',
        'investigation-result'
      ],
      vision: [
        'treatment-plan',
        'history-of-present-illness',
        'patient-history',
        'physical-examination',
        'investigation-result'
      ],
      pharmacy: [
        'treatment-plan',
        'history-of-present-illness',
        'patient-history',
        'physical-examination',
        'investigation-result'
      ]
    }

    const getSuppInfo = cat =>
      values?.supportingInfos?.find(i => i.category === cat)

    const additionalSupportInfos = [
      'treatment-plan',
      'history-of-present-illness',
      'patient-history',
      'physical-examination'
    ].map(category => ({
      category,
      code: getSuppInfo(category)?.code || null,
      code_text: getSuppInfo(category)?.code_text || null,
      start_date: getSuppInfo(category)?.start_date || null,
      end_date: getSuppInfo(category)?.end_date || null,
      value: getSuppInfo(category)?.value || 'none',
      unit: getSuppInfo(category)?.unit || null,
      reason: getSuppInfo(category)?.reason || null,
      image_content_type: getSuppInfo(category)?.image_content_type || null,
      image_title: getSuppInfo(category)?.image_title || null,
      image_creation_date: getSuppInfo(category)?.image_creation_date || null,
      image_value: getSuppInfo(category)?.image_value || null,
      image_minio_path: getSuppInfo(category)?.image_minio_path || null
    }))

    additionalSupportInfos.push({
      category: 'investigation-result',
      code: 'NA',
      code_text: t('Not applicable or Other'),
      start_date: null,
      end_date: null,
      value: null,
      unit: null,
      reason: null,
      image_content_type: '',
      image_title: '',
      image_creation_date: moment().toISOString(),
      image_value: '',
      image_minio_path: null
    })

    const getAdditionalSupportInfosForType = type => {
      const required = requiredCategoriesByType[type] || []
      return additionalSupportInfos.filter(info =>
        required.includes(info.category)
      )
    }

    const isReferralPreAuth = Boolean(
      visitData?.referral || selectedVisitData?.referral
    )
    const referralSupportingInfo = {
      category: 'reason-for-visit',
      code: 'referral',
      code_text: 'Reason for visit'
    }

    const buildSupportingInfo = (type, extraItems = []) => {
      const resolvedExtraItems = isReferralPreAuth
        ? [...extraItems, referralSupportingInfo]
        : extraItems
      const required = requiredCategoriesByType[type] || []
      const allowedCategories = new Set([
        ...required,
        ...resolvedExtraItems.map(i => i.category)
      ])
      const emrCreationDate = sourceEmrData?.created_at
        ? moment(sourceEmrData.created_at).format('YYYY-MM-DD')
        : moment().format('YYYY-MM-DD')
      const converted = convertPreAuthClinicalData(
        [
          ...(values?.supportingInfos || []),
          values?.chiefComplaints,
          ...(values?.vitalSigns || []),
          ...getAdditionalSupportInfosForType(type)
        ],
        emrCreationDate
      ).map(item => {
        if (
          item.category === 'vital-sign-height' &&
          (item.value === '' || item.value === null || item.value === undefined)
        ) {
          return { ...item, value: '170' }
        }
        if (
          item.category === 'vital-sign-weight' &&
          (item.value === '' || item.value === null || item.value === undefined)
        ) {
          return { ...item, value: '70' }
        }
        return item
      })
      const seen = new Set()
      const deduped = []
      for (const item of converted) {
        if (!seen.has(item.category)) {
          seen.add(item.category)
          deduped.push(item)
        }
      }
      return [
        ...deduped.filter(item => allowedCategories.has(item.category)),
        ...resolvedExtraItems
      ]
    }

    const basePayload = {
      is_edit_request: false,
      ref_transaction_id: null,
      is_followup_req: false,
      prv_transaction_id: '',
      patient_id:
        sourceEmrData?.patient?.rcm_ref ||
        selectedPatientData?.rcm_ref ||
        visitData?.patient?.rcm_ref ||
        selectedVisitData?.patient?.rcm_ref ||
        '',
      insurance_plan_id: insurancePlanId,
      is_new_born: false,
      is_referral: false,
      date_ordered: moment().format('YYYY-MM-DD'),
      priority: 'stat',
      subtype: 'op',
      offline_verification_id: null,
      offline_verification_date: null,
      verification_resp_ident_value: null,
      verification_resp_ident_url: null,
      physicians_id: [
        parseInt(
          auth?.roleData?.rcm_ref || visitData?.practitioner?.rcm_ref || 0
        )
      ],
      diagnosis: diagnosisObj || [],
      is_accident: values?.is_accident || false,
      accident_info: {
        street_name: values?.accident?.street || '',
        city: values?.accident?.city || '',
        state: values?.accident?.province || '',
        country: values?.accident?.country || '',
        date: values?.accident?.date
          ? moment(values?.accident?.date).format('YYYY-MM-DD')
          : ''
      },
      tp_ref_no: Number(resolvedEmrId),
      vision_prescription: null,
      is_vision_prescription: false
    }

    const uniqueDaysSupplyValues = [
      ...new Set(
        (itemsObj || [])
          .filter(item => item?.type === 'medication-codes')
          .map(item => Number(item?.days_supply) || 1)
      )
    ]
    const daysSupplySupportingInfos = uniqueDaysSupplyValues.map(dsValue => ({
      category: 'days-supply',
      code: null,
      code_text: null,
      start_date: null,
      end_date: null,
      value: String(dsValue),
      valueUnit: 'd',
      unit: 'd',
      reason: null,
      image_content_type: null,
      image_title: null,
      image_creation_date: null,
      image_value: null,
      image_minio_path: null
    }))
    const pharmacySupportingInfo = buildSupportingInfo(
      'pharmacy',
      daysSupplySupportingInfos
    )

    const pharmacyItems =
      itemsObj?.filter(item => item?.type === 'medication-codes') || []
    const approvalSettings = auth?.insuranceApprovalSettings
    if (
      pharmacyItems.length > 0 &&
      shouldAutoSendApproval(approvalSettings, 'pharmacy')
    ) {
      const pharmacyPayload = {
        ...basePayload,
        type: 'pharmacy',
        supporting_info: pharmacySupportingInfo,
        items: pharmacyItems.map(itm => {
          const itemDaysSupply = String(Number(itm?.days_supply) || 1)
          const matchingDaysSupplyIndex = pharmacySupportingInfo.findIndex(
            si => si.category === 'days-supply' && si.value === itemDaysSupply
          )
          const itemSupportingInfo = pharmacySupportingInfo
            .map((si, i) => ({ si, idx: i + 1 }))
            .filter(
              ({ si, idx }) =>
                si.category !== 'days-supply' ||
                idx === matchingDaysSupplyIndex + 1
            )
            .map(({ idx }) => idx)
          const { days_supply, ...itemWithoutDaysSupply } = itm
          return {
            ...itemWithoutDaysSupply,
            supporting_info: itemSupportingInfo
          }
        }),
        encounter: null
      }
      for (const payload of splitPreAuthPayloadByReferral(pharmacyPayload)) {
        const response = await fetchData('POST', '/v3/RCM/sendPreAuth', payload)
        if (response) {
          const itemIds = payload.items.map(item => item._item_id).filter(Boolean)
          if (itemIds.length > 0) {
            await fetchData('POST', '/v3/emr/updateItemsField', {
              emr_id: resolvedEmrId,
              visit_id: visitData?.visit_id || selectedVisitData?.visit_id,
              patient_id: String(
                selectedPatientData?.patient_id ||
                sourceEmrData?.patient?.patient_id ||
                visitData?.patient?.patient_id ||
                ''
              ),
              item_ids: itemIds
            })
          }
        }
      }
    }

    const isOphthalmologyDept =
      auth.roleData?.department?.name_en?.toLowerCase() === 'ophthalmology' ||
      visitData?.practitioner?.department?.name_en?.toLowerCase() ===
      'ophthalmology' ||
      visitData?.department?.name_en?.toLowerCase() === 'ophthalmology'
    const isDentalDept =
      auth.roleData?.department?.name_en?.toLowerCase() === 'dental' ||
      auth.roleData?.department?.name_en?.toLowerCase() === 'dentistry' ||
      auth.roleData?.department?.name_en?.toLowerCase() === 'oral and dental' ||
      visitData?.practitioner?.department?.name_en?.toLowerCase() === 'dental' ||
      visitData?.practitioner?.department?.name_en?.toLowerCase() ===
      'dentistry' ||
      visitData?.practitioner?.department?.name_en?.toLowerCase() ===
      'oral and dental' ||
      visitData?.department?.name_en?.toLowerCase() === 'dental' ||
      visitData?.department?.name_en?.toLowerCase() === 'dentistry' ||
      visitData?.department?.name_en?.toLowerCase() === 'oral and dental'
    const isInPatientVisit =
      visitData?.visit_category === 'in_patient' ||
      selectedVisitData?.visit_category === 'in_patient'
    const payloadObj2Type = isOphthalmologyDept
      ? 'vision'
      : isDentalDept
        ? 'oral'
        : isInPatientVisit
          ? 'institutional'
          : 'professional'
    const nonPharmacyItems =
      itemsObj?.filter(item => item?.type !== 'medication-codes') || []
    if (
      nonPharmacyItems.length > 0 &&
      shouldAutoSendApproval(approvalSettings, payloadObj2Type)
    ) {
      const supportingInfo = buildSupportingInfo(payloadObj2Type)
      const nonPharmacyPayload = {
        ...basePayload,
        type: payloadObj2Type,
        supporting_info: supportingInfo,
        items: nonPharmacyItems.map(item => ({
          ...item,
          supporting_info: supportingInfo.map((_, i) => i + 1)
        })),
        encounter: null
      }
      for (const payload of splitPreAuthPayloadByReferral(
        nonPharmacyPayload
      )) {
        const response = await fetchData('POST', '/v3/RCM/sendPreAuth', payload)
        if (response) {
          const itemIds = payload.items.map(item => item._item_id).filter(Boolean)
          if (itemIds.length > 0) {
            await fetchData('POST', '/v3/emr/updateItemsField', {
              emr_id: resolvedEmrId,
              visit_id: visitData?.visit_id || selectedVisitData?.visit_id,
              patient_id: String(
                selectedPatientData?.patient_id ||
                sourceEmrData?.patient?.patient_id ||
                visitData?.patient?.patient_id ||
                ''
              ),
              item_ids: itemIds
            })
          }
        }
      }
    }
  }

  const handleSubmit = async values => {
    if (!canUpdateEmr) return
    const resolvedEmrId = emrData?.emr_id || emrData?.id

    if (!resolvedEmrId) {
      toast.error(t('EMR ID is required'))
      return
    }

    setIsSaving(true)
    try {
      const { items } =
        compareEmrItems(emrData?.items || [], values?.items || []) || {}
      const payload = {
        emr_id: Number(resolvedEmrId),
        visit_id: emrData?.visit_id || visitData?.visit_id,
        patient_id: String(
          emrData?.patient_id || visitData?.patient?.patient_id || ''
        ),
        items: (items || values?.items || []).map(normalizeItemNumbers)
      }

      const response = await fetchData('POST', '/v3/emr/items/update', payload)

      if (response) {
        const updatedEmrData = response?.data || emrData
        const formItems = values?.items || []
        const mergedItems = (updatedEmrData?.items || formItems).map(item => {
          const formItem = formItems.find(
            f => f?.item_id === item?.item_id || f?.id === item?.id
          )
          return formItem?.resend_approval
            ? { ...item, resend_approval: true }
            : item
        })
        const approvalValues = {
          ...values,
          accident: updatedEmrData?.accident || values?.accident,
          chiefComplaints:
            updatedEmrData?.medical_assessments?.chief_complaints ||
            values?.chiefComplaints,
          diagnosisArray: updatedEmrData?.diagnosisArr || values?.diagnosisArray,
          is_accident: updatedEmrData?.is_accident || values?.is_accident,
          items: mergedItems,
          supportingInfos:
            updatedEmrData?.supportingInfos || values?.supportingInfos || [],
          vitalSigns: updatedEmrData?.vital_signs || values?.vitalSigns || []
        }
        const shouldRequestPreAuth =
          (updatedEmrData?.patient?.beneficiary_type === 'insurance' ||
            selectedPatientData?.beneficiary_type === 'insurance') &&
          (visitData?.visit_payment_type === 'insurance' ||
            updatedEmrData?.visit_payment_type === 'insurance' ||
            emrData?.visit_payment_type === 'insurance') &&
          (approvalValues?.items || []).some(
            item =>
              item?.is_approval === true &&
              item?.item_category === 'insurance' &&
              (!item?.is_approval_sent || item?.resend_approval === true)
          )

        if (shouldRequestPreAuth) {
          await handleRequestPreAuth(approvalValues, resolvedEmrId, updatedEmrData)
        }
        toast.success(t('Items updated successfully'))
        await onUpdated?.(
          (updatedEmrData?.items || values?.items || []).map(item => ({
            ...item,
            resend_approval: false
          }))
        )
        onClose?.()
      }
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {({ handleSubmit }) => (
        <Form onSubmit={handleSubmit} className='flex flex-col gap-4'>
          <EmrOrderItem
            selectedPatientData={selectedPatientData}
            visitData={visitData}
            emrFormData={emrData}
            coverageBool={false}
            design='new'
            skipDiagnosisRequired
          />

          <div className='flex justify-end gap-2 border-t border-gray-100 pt-3'>
            <CustomButton
              label='Cancel'
              buttonType='reset'
              onClick={onClose}
              disabled={isSaving}
            />
            {canUpdateEmr && <CustomButton
              type='submit'
              label='Update Items'
              buttonType='success'
              loading={isSaving}
              loadingText='Updating'
            />}
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default EditEmrItemsModal
